import {
  type PopupImportFunnelOption,
  findFunnelOptionByImportedId,
  findLandingPageForFunnel,
  findPageOptionByIdForFunnel,
  findPageOptionByUrlForFunnel,
  normalizeImportString,
  pageImportPickerValue,
} from "./popupImportFunnelOptions";
import type { PopupImportRowValues } from "./validationSchema";

export type PopupImportVerificationResolutionHooks = {
  onInvalidFunnel?: (index: number) => void;
  onClearFunnelMessage?: (index: number) => void;
  onClearPageWhenNoFunnel?: (index: number) => void;
  onResolvedPage?: (index: number) => void;
  onInvalidPage?: (index: number) => void;
  onClearPageOnly?: (index: number) => void;
};

/**
 * Canonicalizes funnel ids and resolves page id + canonical URL from CSV/import fields.
 * Same rules as `PopupImportVerificationSync`; optional hooks drive resolution UI messages.
 */
export function applyPopupImportVerificationResolution(
  popups: PopupImportRowValues[],
  funnelOptions: PopupImportFunnelOption[],
  hooks?: PopupImportVerificationResolutionHooks
): { next: PopupImportRowValues[]; changed: boolean } {
  const next = popups.map((r) => ({ ...r }));
  let changed = false;

  for (let i = 0; i < next.length; i++) {
    const rawFid = next[i].verificationFunnelId;
    const trimmedFid = (rawFid ?? "").trim();
    const funnel = findFunnelOptionByImportedId(trimmedFid, funnelOptions);
    const canonicalId = funnel?.id ?? "";
    const fidNorm = normalizeImportString(rawFid);

    if (fidNorm && !canonicalId) {
      next[i] = {
        ...next[i],
        verificationFunnelId: "",
        funnelName: "",
        verificationPageUrl: "",
        verificationPageId: "",
      };
      changed = true;
      hooks?.onInvalidFunnel?.(i);
      continue;
    }

    if (canonicalId && trimmedFid !== canonicalId) {
      next[i] = {
        ...next[i],
        verificationFunnelId: canonicalId,
        funnelName: funnel?.name ?? next[i].funnelName,
      };
      changed = true;
    }

    if (canonicalId) {
      hooks?.onClearFunnelMessage?.(i);
    }

    const fid = (next[i].verificationFunnelId ?? "").trim();
    const rawPurl = next[i].verificationPageUrl;
    const rawPid = next[i].verificationPageId ?? "";
    const purlNorm = normalizeImportString(rawPurl);
    const pidTrim = rawPid.trim();

    if (!fid) {
      if (purlNorm || pidTrim) {
        next[i] = {
          ...next[i],
          verificationPageUrl: "",
          verificationPageId: "",
        };
        changed = true;
      }
      hooks?.onClearPageWhenNoFunnel?.(i);
      continue;
    }

    const pageByUrl =
      purlNorm && findPageOptionByUrlForFunnel(funnelOptions, fid, rawPurl);
    const pageById =
      pidTrim && findPageOptionByIdForFunnel(funnelOptions, fid, rawPid);

    let resolved = pageByUrl ?? pageById ?? null;

    if (
      pageByUrl &&
      pageById &&
      pageImportPickerValue(pageByUrl) !== pageImportPickerValue(pageById)
    ) {
      resolved = pageByUrl;
    }

    if (resolved) {
      const pickerId = pageImportPickerValue(resolved);
      if (
        next[i].verificationPageId !== pickerId ||
        next[i].verificationPageUrl !== resolved.url
      ) {
        next[i] = {
          ...next[i],
          verificationPageId: pickerId,
          verificationPageUrl: resolved.url,
        };
        changed = true;
      }
      hooks?.onResolvedPage?.(i);
      continue;
    }

    const landing = findLandingPageForFunnel(funnel);
    if (landing) {
      const pickerId = pageImportPickerValue(landing);
      if (
        next[i].verificationPageId !== pickerId ||
        next[i].verificationPageUrl !== landing.url
      ) {
        next[i] = {
          ...next[i],
          verificationPageId: pickerId,
          verificationPageUrl: landing.url,
        };
        changed = true;
      }
      hooks?.onResolvedPage?.(i);
    } else if (purlNorm || pidTrim) {
      next[i] = {
        ...next[i],
        verificationPageUrl: "",
        verificationPageId: "",
      };
      changed = true;
      hooks?.onInvalidPage?.(i);
    } else {
      hooks?.onClearPageOnly?.(i);
    }
  }

  return { next, changed };
}
